import * as React from "react";
import { useState, useCallback } from "react";
import styled, { css } from "styled-components";
import { useQueryClient } from "@tanstack/react-query";
import { COLLECTIONS_QUERY_KEY } from "collections/hooks/useCollections";
import { BootstrapModalConfirm, Col, MenuItem, DeleteMenuItem } from "components/bootstrap";
import { useGetLinkedProceduresForStep, useDeleteEventProcedureStep } from "security-app/hooks/useEventProceduresAPI";
import { ConfirmDialog, Icon, IfPermitted, HasOwnership } from "components/common";
import { useModalContext } from "common/contexts";
import EntityShareModal from "components/permissions/EntityShareModal";
import usePluggableEntitySharedActions from "hooks/usePluggableEntitySharedActions";
import { MoreActionsMenu } from "components/common/MoreActions";
const Wrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${theme.spacings.xs};
  `
);
const StyledCol = styled(Col)`
  display: flex;
  flex-direction: column;
  padding: 0.6rem;
  gap: 0.3rem;
`;
function RowActionMenu({ eventProcedureStep }) {
  const { setModal, setEntity } = useModalContext();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteWarningModal, setShowDeleteWarningModal] = useState(false);
  const [showEntityShareModal, setShowEntityShareModal] = useState(false);
  const queryClient = useQueryClient();
  const { linkedEventProcedures, loadingLinkedEventProcedures } = useGetLinkedProceduresForStep(eventProcedureStep.id);
  const { deleteEventProcedureStep } = useDeleteEventProcedureStep();
  const { actions: pluggableActions, actionModals: pluggableActionModals } = usePluggableEntitySharedActions(
    eventProcedureStep,
    "event_procedure_step",
    () => queryClient.invalidateQueries({ queryKey: [COLLECTIONS_QUERY_KEY] })
  );
  const moreActions = [pluggableActions.length ? pluggableActions : null].filter(Boolean);
  const onEdit = () => {
    setEntity(eventProcedureStep);
    setModal("EDIT-EVENT-PROCEDURE-STEP");
  };
  const onDelete = async () => {
    await deleteEventProcedureStep({ id: eventProcedureStep.id });
    setShowDeleteModal(false);
  };
  const handleDelete = () => {
    if (loadingLinkedEventProcedures) return;
    if (linkedEventProcedures.length < 1) {
      setShowDeleteModal(true);
    } else {
      setShowDeleteWarningModal(true);
    }
  };
  const toggleEntityShareModal = useCallback(() => {
    setShowEntityShareModal((cur) => !cur);
  }, []);
  return /* @__PURE__ */ React.createElement(Wrapper, null, /* @__PURE__ */ React.createElement(MoreActionsMenu, { title: "Toggle event procedure actions" }, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: [`event_procedure_steps:edit:${eventProcedureStep.id}`] }, /* @__PURE__ */ React.createElement(MenuItem, { onClick: onEdit }, /* @__PURE__ */ React.createElement(Icon, { name: "edit_square", size: "xs" }), " Edit")), /* @__PURE__ */ React.createElement(HasOwnership, { id: eventProcedureStep.id, type: "event_procedure_step" }, ({ disabled: hasMissingPermissions }) => /* @__PURE__ */ React.createElement(MenuItem, { disabled: hasMissingPermissions, onClick: toggleEntityShareModal }, /* @__PURE__ */ React.createElement(Icon, { name: "person_add", size: "xs" }), " Share")), moreActions, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: [`event_procedure_steps:delete:${eventProcedureStep.id}`] }, /* @__PURE__ */ React.createElement(DeleteMenuItem, { onClick: () => handleDelete() }, /* @__PURE__ */ React.createElement(Icon, { name: "delete", size: "xs" }), " Delete"))), /* @__PURE__ */ React.createElement(
    BootstrapModalConfirm,
    {
      title: "Are you sure you want to delete this step?",
      onConfirm: onDelete,
      onCancel: () => setShowDeleteModal(false),
      showModal: showDeleteModal
    },
    /* @__PURE__ */ React.createElement("div", null, "You are about to delete this event procedure step: ", eventProcedureStep.title)
  ), /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      show: showDeleteWarningModal,
      title: "This step is currently being used in procedures",
      onConfirm: () => setShowDeleteWarningModal(false),
      hideCancelButton: true,
      btnConfirmText: "Okay"
    },
    /* @__PURE__ */ React.createElement("div", null, "This step is currently being used in the following procedures:"),
    /* @__PURE__ */ React.createElement(StyledCol, null, linkedEventProcedures.map((procedure) => /* @__PURE__ */ React.createElement("strong", { key: `procedure-item-${procedure}` }, "\u2022 ", procedure))),
    /* @__PURE__ */ React.createElement("div", null, "If you want to delete this step please first remove them from the procedures above.")
  ), showEntityShareModal && /* @__PURE__ */ React.createElement(
    EntityShareModal,
    {
      entityId: eventProcedureStep.id,
      entityType: "event_procedure_step",
      entityTypeTitle: "Event Procedure Step",
      entityTitle: eventProcedureStep.title,
      showShareableEntityURL: false,
      description: "Search for a User or Team to add as collaborator on this event procedure step.",
      onClose: toggleEntityShareModal
    }
  ), pluggableActionModals);
}
export default RowActionMenu;
